import { useState } from 'react';
import {
  Chip,
  Column,
  ColumnType,
  Flex,
  IconButton,
} from '@elementary/common-ui/components';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import dayjs from 'dayjs';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/trash.svg';
import { Tooltip } from '@mui/material';
import { expireOptions } from './constants';
import { Token } from './types';
import DeleteTokenDialog from './DeleteTokenDialog';

const getExpirationLabel = (
  expiresDate: string | null,
  createdDate: string,
): string => {
  if (!expiresDate) {
    return 'Never';
  }

  const created = dayjs(createdDate);
  const expires = dayjs(expiresDate);
  const minutesDiff = expires.diff(created, 'minute');

  const option = expireOptions.find(
    (opt) => opt.value === minutesDiff.toString(),
  );
  if (option) {
    return option.label;
  }

  const differences = expireOptions
    .filter((opt) => opt.value !== 'never')
    .map((opt) => ({
      ...opt,
      diff: Math.abs(parseInt(opt.value) - minutesDiff),
    }))
    .sort((a, b) => a.diff - b.diff);

  return differences[0].label;
};

interface DeleteTokenButtonProps {
  token: Token;
  onDelete: (tokenId: string) => Promise<void>;
  isDeleting: boolean;
  tokenType: 'personal' | 'account';
}

const DeleteTokenButton = ({
  token,
  onDelete,
  isDeleting,
  tokenType,
}: DeleteTokenButtonProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <Flex sx={{ justifyContent: 'flex-end' }}>
      <Tooltip title="Delete token" placement="left">
        <IconButton onClick={() => setIsDeleteDialogOpen(true)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <DeleteTokenDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        token={token}
        onDelete={onDelete}
        isDeleting={isDeleting}
        tokenType={tokenType}
      />
    </Flex>
  );
};

export const useTokensColumns = (
  onDelete: (tokenId: string) => Promise<void>,
  isDeleting: boolean,
  tokenType: 'personal' | 'account',
) => {
  const columns: Column<Token>[] = [
    {
      id: 'description',
      header: 'Description',
      isFilterCounterHidden: true,
    },
    {
      id: 'createdAt',
      header: 'Created',
      type: ColumnType.Date,
      renderer: (token) => dateRenderer(token.createdAt, 'L'),
      compareFunc: (a, b) => dateComparer(a.createdAt, b.createdAt),
      filterValueMatcher: ([from, to, emptyFlag], rowData) => {
        const time = rowData.createdAt;

        if (emptyFlag === 'true') return !time;
        if (!time) return false;

        const timeDayjs = dayjs(time);

        return (
          (!from || dayjs(from).startOf('day').isBefore(timeDayjs)) &&
          (!to || dayjs(to).endOf('day').isAfter(timeDayjs))
        );
      },
    },
    {
      id: 'expires',
      header: 'Expires',
      renderer: (token) => {
        const label = getExpirationLabel(token.expires, token.createdAt);
        return <Chip sx={{ px: '12px !important' }} label={label} />;
      },
      showValueWhenEmpty: true,
      compareFunc: (a, b) => dateComparer(a.expires, b.expires),
      sortFilterOptions: false,
      filterOptionsOverride: expireOptions.map((option) => ({
        label: option.label,
        value: option.label.toLowerCase(),
      })),
      filterValueMatcher: (selectedFilterValues, rowData) => {
        const label = getExpirationLabel(rowData.expires, rowData.createdAt);
        return selectedFilterValues.includes(label.toLowerCase());
      },
    },
    {
      id: 'delete',
      header: '',
      renderer: (token) => (
        <DeleteTokenButton
          token={token}
          onDelete={onDelete}
          isDeleting={isDeleting}
          tokenType={tokenType}
        />
      ),
      filterable: false,
      sortable: false,
      metaColumn: true,
    },
  ];

  return columns;
};
